import { capabilityEditorial } from './capability-editorial/capabilities';
import { issueContexts } from './capability-editorial/issue-contexts';
import { editorialSources } from './capability-editorial/sources';
import { findBinding, bindingPendingOwner } from './scenario-capability-explainers';
import { entryStudyRelation } from './scenario-research-reference';
import { unboundCapabilityReading } from './scenario-reading-details';

export type EditorialTopic = (typeof capabilityEditorial.capabilities)[number];
export type EditorialSource = (typeof editorialSources)[number];
export interface CapabilityReading {
  readonly title: string;
  readonly purpose: string;
  readonly output: string;
  readonly meaning: string;
  readonly method: string;
  readonly limit: string;
  readonly condition: string;
  readonly topic: EditorialTopic | undefined;
  readonly pending: boolean;
  readonly scopeNote: string | undefined;
}

/** Existing reviewed TOPICS to reading topics; never writes an official capability ID. */
const topicKeys: Readonly<Record<string, string>> = {
  'ux03-ventilation': 'HR-SMOKE',
  'ux03-aerial-observation': 'HR-THERMAL',
  'ux03-indoor-mapping': 'HR-INDOOR',
  'ux03-communications': 'HR-COMMS',
  'ux03-assisted-transfer': 'HR-TRANSFER',
  'ux03-remote-suppression': 'HR-REMOTE',
};
const issueKeys: Readonly<Record<string, string>> = {
  'HRF-HZ-02': 'HR-ISSUE-01',
  'HRF-HZ-03': 'HR-ISSUE-02',
  'HRF-HZ-01': 'HR-ISSUE-03',
  'HRF-HZ-05': 'HR-ISSUE-04',
  'HRF-HZ-06': 'HR-ISSUE-05',
  'HRF-HZ-04': 'HR-ISSUE-06',
};
export function editorialTopic(key: string) {
  return capabilityEditorial.capabilities.find((row) => row.key === key);
}
export function sourcesForEditorial(topic: EditorialTopic): readonly EditorialSource[] {
  return topic.sourceIds.flatMap((id) => editorialSources.filter((row) => row.id === id));
}
export function forestCapabilityReading(
  issueKey: string,
  original: {
    key: string;
    name: string;
    purpose: string;
    output: string;
    method: string;
    limit: string;
  },
): CapabilityReading {
  const topic = editorialTopic(original.key);
  const context = issueContexts.forest
    .find((row) => row.issueKey === issueKey)
    ?.capabilities.find((row) => row.key === original.key);
  return {
    title: topic?.title ?? original.name,
    purpose: context?.purpose ?? original.purpose,
    output: context?.output ?? original.output,
    meaning: topic?.meaning ?? original.purpose,
    method: topic?.method ?? original.method,
    limit: topic?.limits ?? original.limit,
    condition: original.limit,
    topic,
    pending: false,
    scopeNote:
      original.key === 'CAP-COMMS' || original.key === 'CAP-RELAY'
        ? '通信回传与临时中继共用通信组网技术图，具体链路与测通结果须分别核查。'
        : undefined,
  };
}
export function highriseCapabilityReading(
  issue: string,
  code: string,
  title: string,
): CapabilityReading {
  const binding = findBinding(issue, code);
  const pending = bindingPendingOwner(issue, code);
  const topic =
    !pending && binding?.topicKey ? editorialTopic(topicKeys[binding.topicKey] ?? '') : undefined;
  const original = entryStudyRelation(issue, 'capability', code);
  const fallback = unboundCapabilityReading[code];
  const context = issueContexts.highrise
    .find((row) => row.issueKey === issueKeys[issue])
    ?.capabilities.find((row) => row.key === topic?.key);
  return {
    title,
    purpose: context?.purpose ?? original?.why ?? '该需求尚缺逐项用途说明。',
    output:
      (topic?.key === 'HR-SMOKE' || topic?.key === 'HR-REMOTE' ? topic.outputs : context?.output) ??
      topic?.outputs ??
      fallback?.output ??
      '待专业核实的人员或结构状态记录；不形成定位、救出或安全结论。',
    meaning: topic?.meaning ?? original?.why ?? title,
    method:
      topic?.method ??
      fallback?.implementation ??
      '由具备资质的专业人员组织核查；完整技术方式仍待内容 Owner 审定。',
    limit:
      topic?.limits ??
      fallback?.limit ??
      '当前研究说明不覆盖完整能力，不作为生命搜索或结构安全评估结论。',
    condition: original?.expectation ?? '具体适用条件待专业核实。',
    topic,
    pending,
    scopeNote: binding?.scopeNote,
  };
}

/** Supplied images are selected by exact manifest path. No category/name guessing. */
const suppliedImages = import.meta.glob('./assets/capability-editorial/**/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;
export function editorialImage(path: string) {
  return suppliedImages[path.replace('04_MEDIA/', './assets/capability-editorial/')];
}

/** Supporting methods only; explicitly separate from the existing F2 requirement. */
export function supplementaryTopics(issue: string, code: string): readonly EditorialTopic[] {
  const keys =
    code === 'HRF-CR-08'
      ? ['HR-PLANS', 'HR-MOVEMENT']
      : code === 'HRF-CR-04' && issue === 'HRF-HZ-03'
        ? ['HR-SEARCH']
        : code === 'HRF-CR-15'
          ? ['HR-SPACE']
          : [];
  return keys.flatMap((key) => {
    const row = editorialTopic(key);
    return row ? [row] : [];
  });
}
